/**
 * Control instrumentation — single source of truth for a control's stable
 * selector (`tid.*`) and the analytics event (`a.*`) it emits.
 *
 * Why: selectors and analytics events were independent strings that drifted.
 * Co-locating them means (1) the `data-testid` and the `Tracker.track(...)` call
 * for one control reference the same root, and (2) ThiaQ can assert a behavioral
 * contract — *driving `control.tid` should emit `control.event`* — a far stronger
 * oracle than "an element became visible".
 *
 * Naming (both dotted `<area>.<surface>.<leaf>`, distinct prefixes on purpose):
 *   - `tid`   → the `data-testid`. Leaf is an **imperative element name**: `send`, `confirm`.
 *   - `event` → the `a.*` analytics event. Leaf is an **outcome**: `submitted`, `confirmed`.
 * The tid and event leaves intentionally differ — a button (noun) triggers an
 * outcome (verb) — but they share the `<area>.<surface>` stem so they stay aligned.
 *
 * Cardinality is not 1:1 by design: many controls emit no event (`event: null`),
 * and one event can be emitted from several call sites (reference the same
 * constant from each). Prefix stays split because "can I locate it" and "did it
 * fire" are different guarantees.
 */

export interface Control {
  /** Stable `data-testid` for locating the control. */
  readonly tid: string;
  /** Canonical `a.*` event emitted when the control is actioned, or null. */
  readonly event: string | null;
}

// Overloaded so `event` is precisely `string` when an outcome is given and
// `null` otherwise — call sites get exact types without a non-null assertion.
export function control(area: string, surface: string, element: string): { readonly tid: string; readonly event: null };
export function control(area: string, surface: string, element: string, outcome: string): { readonly tid: string; readonly event: string };
export function control(area: string, surface: string, element: string, outcome?: string): Control {
  return {
    tid: `tid.${area}.${surface}.${element}`,
    event: outcome ? `a.${area}.${surface}.${outcome}` : null,
  };
}

/**
 * Registry of instrumented controls. Add an entry when you give a control a
 * stable selector and/or wire its analytics event, then reference the entry
 * from both the JSX (`data-testid={CONTROLS.x.y.tid}`) and the tracker
 * (`Tracker.track(CONTROLS.x.y.event)`).
 */
export const CONTROLS = {
  ai: {
    /** AI prompt textarea. Emits no event (typing is not tracked). tid wired in prompt-input.tsx. */
    promptInput: control('ai', 'prompt', 'input'),
    /**
     * AI prompt submit button. tid wired in prompt-input.tsx.
     * `a.ai.prompt.submitted` is the candidate event name if/when submit tracking
     * is added — intentionally NOT fired yet (would be new analytics behavior).
     */
    promptSend: control('ai', 'prompt', 'send'),
  },
} as const;
